/* global Blockly */
import PropTypes from 'prop-types';
import util from "../../../../../../utils/es5Utils";
import { buttonImages, conditionOptions } from '../../../../../../blocklyConstants';
import { createConditionHelpers } from '../../../../../../blockly/conditionBlockHelpers';

export const BLOCK_NAME = 'snap_tableview_highlight_V3';

function defineBlock(context) {
    return {
        conditionsNumber: 0,
        expandableAfterNumber: 1,
        isExpanded: false,
        target: 'COLUMN',
        hasSecondColor: false,
        compareOperators: conditionOptions.compareOperators,
        logicalOperators: conditionOptions.logicalOperators,
        columnNames: conditionOptions.defaultColumnNames,

        init: function () {
            let selectTableDropdown = new Blockly.FieldWidgetsDropdown(context, 'tableview', undefined, '- select -', this.updateWidget_.bind(this));
            let targetDropdown = new Blockly.FieldDropdown(
                [['column', 'COLUMN'], ['row', 'ROW'], ['cell', 'CELL']],
                this.onTargetChanged_.bind(this)
            );

            this.appendDummyInput('WIDGET')
                .appendField("highlight")
                .appendField(targetDropdown, "TARGET")
                .appendField("in")
                .appendField(selectTableDropdown, "ID");

            this.addColorSection_();
            this.addColumnNameInput_();

            this.setPreviousStatement(true, null);
            this.setNextStatement(true, null);
            this.setColour(Blockly.Msg.WIDGET_VALUES_HUE);
            this.setTooltip('Highlight a column, row, or cell in a TableView widget based on conditions.');
            this.setHelpUrl(`${Blockly.BASE_HELP_URL}#Table`);
        },

        onLoad: function () {
            if (this.target !== 'COLUMN') {
                for (let i = 1; i <= this.conditionsNumber; i++) {
                    let columnField = this.getField('COLUMN_NAME_' + i);
                    if (columnField) {
                        columnField.validate();
                    }
                }
            } else if (this.getField('HIGHLIGHT_COLUMN')) {
                this.getField('HIGHLIGHT_COLUMN').validate();
            }
        },

        onTargetChanged_: function (newTarget) {
            if (newTarget === this.target) return newTarget;

            this.removeMiddleInputs_();
            this.target = newTarget;

            if (newTarget === 'COLUMN') {
                this.addColumnNameInput_();
            } else {
                this.addConditionInputs_();
            }

            return newTarget;
        },

        removeMiddleInputs_: function () {
            if (this.getInput('COLUMN_TARGET')) {
                this.removeInput('COLUMN_TARGET');
            }

            while (this.conditionsNumber > 0) {
                this.removeCondition_();
            }

            if (this.getInput('TOGGLE_BTN')) {
                this.removeInput('TOGGLE_BTN');
            }
            if (this.getInput('PLUS_MINUS')) {
                this.removeInput('PLUS_MINUS');
            }

            this.isExpanded = false;
        },

        addColumnNameInput_: function () {
            this.appendDummyInput('COLUMN_TARGET')
                .appendField("column")
                .appendField(new Blockly.FieldDropdown(this.columnNames), 'HIGHLIGHT_COLUMN');

            this.moveInputBefore('COLUMN_TARGET', 'COLOR_1');

            if (this.rendered) {
                this.render();
            }
        },

        addConditionInputs_: function () {
            this.appendDummyInput("TOGGLE_BTN")
                .appendField(new Blockly.FieldImage(buttonImages.expandImage, 20, 20, "expand", function () { this.toggleConditionsVisibility_(); }.bind(this)), "expand")
                .appendField(new Blockly.FieldImage(buttonImages.collapseImage, 20, 20, "collapse", function () { this.toggleConditionsVisibility_(); }.bind(this)), "collapse");

            this.appendDummyInput('PLUS_MINUS')
                .appendField(" ")
                .appendField(new Blockly.FieldImage(buttonImages.minusImage, 24, 24, "*", this.removeCondition_.bind(this)), "MINUS")
                .appendField(new Blockly.FieldImage(buttonImages.plusImage, 24, 24, "*", this.addCondition_.bind(this)), "PLUS");

            this.moveInputBefore('TOGGLE_BTN', 'COLOR_1');
            this.moveInputBefore('PLUS_MINUS', 'COLOR_1');

            this.addCondition_();

            this.hideExpandCollapseButtons_();
            this.getField('MINUS').setVisible(false);
        },

        addColorSection_: function () {
            let colorInput = this.appendValueInput('COLOR_1')
                .appendField(new Blockly.FieldDropdown([['text color', 'TEXT'], ['background color', 'BG']], this.onColorTypeChanged_.bind(this)), 'COLOR_TYPE_1');

            let picker = this.createColorPicker_('#000000');
            colorInput.connection.connect(picker.outputConnection);

            this.appendDummyInput('COLOR_BTN')
                .appendField(" ")
                .appendField(new Blockly.FieldImage(buttonImages.minusImage, 24, 24, "*", this.removeSecondColor_.bind(this)), "COLOR_MINUS")
                .appendField(new Blockly.FieldImage(buttonImages.plusImage, 24, 24, "*", this.addSecondColor_.bind(this)), "COLOR_PLUS");

            this.getField('COLOR_MINUS').setVisible(false);
        },

        createColorPicker_: function (defaultColour) {
            let picker = this.workspace.newBlock("colour_picker");
            picker.setShadow(true);
            picker.setMovable(false);
            picker.setFieldValue(defaultColour, 'COLOUR');

            if (this.rendered) {
                picker.initSvg();
                picker.render();
            }

            return picker;
        },

        addSecondColor_: function () {
            if (this.hasSecondColor) return;
            this.hasSecondColor = true;

            let secondType = this.getFieldValue('COLOR_TYPE_1') === 'TEXT' ? 'BG' : 'TEXT';
            let label = secondType === 'TEXT' ? 'text color' : 'background color';
            let defaultColour = secondType === 'TEXT' ? '#000000' : '#FFFFFF';

            let colorInput = this.appendValueInput('COLOR_2')
                .appendField(new Blockly.FieldLabel(label), 'COLOR_LABEL_2');

            let picker = this.createColorPicker_(defaultColour);
            colorInput.connection.connect(picker.outputConnection);

            this.moveInputBefore('COLOR_2', 'COLOR_BTN');

            this.getField('COLOR_PLUS').setVisible(false);
            this.getField('COLOR_MINUS').setVisible(true);

            if (this.rendered) {
                this.render();
            }
        },

        removeSecondColor_: function () {
            if (!this.hasSecondColor) return;

            this.removeInput('COLOR_2');
            this.hasSecondColor = false;

            this.getField('COLOR_PLUS').setVisible(true);
            this.getField('COLOR_MINUS').setVisible(false);

            if (this.rendered) {
                this.render();
            }
        },

        onColorTypeChanged_: function (newValue) {
            if (this.hasSecondColor) {
                let secondLabel = newValue === 'TEXT' ? 'background color' : 'text color';
                this.getField('COLOR_LABEL_2').setValue(secondLabel);
            }

            return newValue;
        },

        updateWidget_: function (selectedTableId) {
            if (selectedTableId === '- select -') return;

            this.getTableColumns_(selectedTableId);

            if (this.target === 'COLUMN') {
                if (this.getInput('COLUMN_TARGET')) {
                    this.removeInput('COLUMN_TARGET');
                }
                this.addColumnNameInput_();
            } else {
                let conditionsNum = this.conditionsNumber;
                while (this.conditionsNumber > 0) {
                    this.removeCondition_();
                }
                for (let i = 1; i <= conditionsNum; i++) {
                    this.addCondition_();
                }
            }
        },

        ...createConditionHelpers(context),

        addCondition_: function () {
            this.conditionsNumber++;

            let condition = this.appendValueInput(`CONDITION_${this.conditionsNumber}`)
                .appendField(" ");

            // In cell mode the column is chosen once (first condition) and reused for the rest.
            if (this.target !== 'CELL' || this.conditionsNumber === 1) {
                condition.appendField(new Blockly.FieldDropdown(this.columnNames), `COLUMN_NAME_${this.conditionsNumber}`, '- select -');
            }

            condition
                .appendField(new Blockly.FieldDropdown(this.compareOperators), `COMPARE_OPERATOR_${this.conditionsNumber}`)
                .setOnNewRow(this.conditionsNumber > this.expandableAfterNumber);

            this.moveInputBefore(condition.name, "PLUS_MINUS");

            if (this.conditionsNumber > 1) {
                this.appendDummyInput(`CONDITION_LOGICAL_OPERATOR_${this.conditionsNumber}`)
                    .appendField(" ")
                    .appendField(new Blockly.FieldDropdown(this.logicalOperators), `LOGICAL_OPERATOR_${this.conditionsNumber}`);
                this.moveInputBefore(`CONDITION_LOGICAL_OPERATOR_${this.conditionsNumber}`, condition.name);
            }

            if (this.conditionsNumber == 2) {
                this.getField('MINUS').setVisible(true);
            }

            if (this.conditionsNumber == this.expandableAfterNumber + 1) {
                this.isExpanded = true;
                this.toggleExpandCollapseButtonsVisibility_();

                for (let i = this.expandableAfterNumber; i >= 1; i--) {
                    this.getInput(`CONDITION_${i}`).setOnNewRow(true);
                }
            }

            let inputBlock = this.workspace.newBlock("fixed_width_text");
            inputBlock.setDisplayWidth(9);
            inputBlock.setShadow(true);
            inputBlock.setMovable(false);

            condition.connection.connect(inputBlock.outputConnection);

            if (this.rendered) {
                inputBlock.initSvg();
                inputBlock.render();
                this.initSvg();
                this.render();
            }
        },

        removeCondition_: function () {
            this.removeInput(`CONDITION_${this.conditionsNumber}`);

            if (this.conditionsNumber > 1) {
                this.removeInput(`CONDITION_LOGICAL_OPERATOR_${this.conditionsNumber}`);
            }

            if (this.conditionsNumber == 2) {
                this.getField('MINUS').setVisible(false);
            }

            if (this.conditionsNumber == this.expandableAfterNumber + 1) {
                this.hideExpandCollapseButtons_();

                for (let i = 1; i < this.expandableAfterNumber; i++) {
                    this.getInput(`CONDITION_${i}`).setOnNewRow(false);
                    this.moveInputBefore(`CONDITION_${i}`, `CONDITION_${i + 1}`);

                    this.getInput(`CONDITION_LOGICAL_OPERATOR_${i + 1}`).setOnNewRow(false);
                    this.moveInputBefore(`CONDITION_LOGICAL_OPERATOR_${i + 1}`, `CONDITION_${i + 1}`);
                }

                this.getInput(`CONDITION_${this.expandableAfterNumber}`).setOnNewRow(false);
                this.moveInputBefore(`CONDITION_${this.expandableAfterNumber}`, `PLUS_MINUS`);
            }

            if (this.rendered) {
                this.render();
            }

            this.conditionsNumber--;
        },

        mutationToDom: function () {
            let container = Blockly.utils.xml.createElement('mutation');
            container.setAttribute('conditionsNumber', this.conditionsNumber);
            container.setAttribute('target', this.target);
            container.setAttribute('hasSecondColor', this.hasSecondColor);
            container.setAttribute('ID', this.getFieldValue('ID'));
            return container;
        },

        domToMutation: function (xml) {
            let conditionsNumber = Number.parseInt(xml.getAttribute("conditionsNumber"));
            let target = xml.getAttribute("target") || 'COLUMN';
            let hasSecondColor = xml.getAttribute("hasSecondColor") === 'true';
            let ID = xml.getAttribute("ID");

            this.getTableColumns_(ID);

            this.removeMiddleInputs_();
            this.target = target;

            if (this.getField('TARGET')) {
                this.getField('TARGET').setValue(target);
            }

            if (target === 'COLUMN') {
                this.addColumnNameInput_();
            } else {
                this.addConditionInputs_();

                while (this.conditionsNumber > 0) {
                    this.removeCondition_();
                }

                for (let i = 0; i < conditionsNumber; i++) {
                    this.addCondition_();
                }
            }

            if (hasSecondColor && !this.hasSecondColor) {
                this.addSecondColor_();
            } else if (!hasSecondColor && this.hasSecondColor) {
                this.removeSecondColor_();
            }
        }
    }
}

function defineGenerators() {
    return {
        'JavaScript': function (block) {
            let id = block.getFieldValue('ID');
            let target = block.getFieldValue('TARGET');

            let columnId = 'null';
            let columnsList = 'null';
            let comparesList = 'null';
            let inputsList = 'null';
            let conditionsList = 'null';

            if (target === 'COLUMN') {
                columnId = `"${block.getFieldValue('HIGHLIGHT_COLUMN')}"`;
            } else {
                let columns = [];
                let compares = [];
                let inputs = [];
                let conditions = [];

                for (let i = 1; i <= this.conditionsNumber; i++) {
                    // In cell mode every condition reuses the column chosen on the first condition.
                    let columnName = target === 'CELL' ? block.getFieldValue('COLUMN_NAME_1') : block.getFieldValue('COLUMN_NAME_' + i);
                    columns.push(`"${columnName}"`);
                    compares.push(`"${block.getFieldValue('COMPARE_OPERATOR_' + i)}"`);
                    inputs.push(Blockly.JavaScript.valueToCode(block, 'CONDITION_' + i, Blockly.JavaScript.ORDER_NONE));
                }

                for (let j = 2; j <= this.conditionsNumber; j++) {
                    conditions.push(`"${block.getFieldValue('LOGICAL_OPERATOR_' + j)}"`);
                }

                columnsList = `[${columns.join(', ')}]`;
                comparesList = `[${compares.join(', ')}]`;
                inputsList = `[${inputs.join(', ')}]`;
                conditionsList = `[${conditions.join(', ')}]`;
            }

            let color1Type = block.getFieldValue('COLOR_TYPE_1');
            let color1 = Blockly.JavaScript.valueToCode(block, 'COLOR_1', Blockly.JavaScript.ORDER_ATOMIC) || 'null';

            let textColor = 'null';
            let backgroundColor = 'null';

            if (color1Type === 'TEXT') {
                textColor = color1;
            } else {
                backgroundColor = color1;
            }

            if (this.hasSecondColor) {
                let color2 = Blockly.JavaScript.valueToCode(block, 'COLOR_2', Blockly.JavaScript.ORDER_ATOMIC) || 'null';
                if (color1Type === 'TEXT') {
                    backgroundColor = color2;
                } else {
                    textColor = color2;
                }
            }

            let code = `Snap.widgets.tableview.highlightRecords("${id}", "${target}", ${columnId}, ${columnsList}, ${comparesList}, ${inputsList}, ${conditionsList}, ${textColor}, ${backgroundColor});\n`;
            return code;
        }
    }
}

const Highlight = function (props) {
    const name = BLOCK_NAME;
    Highlight.blockName = name;

    Blockly.Blocks[name] = defineBlock(props.context);
    let generators = defineGenerators();
    for (let i in generators) {
        Blockly[i][name] = generators[i];
    }

    let widgets = util.widgetsOfType(props.context, "tableview");

    if (widgets.length < 1) {
        return null;
    }

    return (
        <block type={name}>
            {
                props.widget?.id === undefined
                    ? null
                    : <field name="ID">{props.widget.id}</field>
            }
        </block>
    );
}

Highlight.propTypes = {
    context: PropTypes.object,
    widget: PropTypes.object
};

export default Highlight;
